import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { supabase } from '../../config/supabase';
import ReactECharts from 'echarts-for-react';
import { format, subDays, subMonths } from 'date-fns';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiUsers, FiBarChart2, FiTrendingUp, FiActivity, FiCalendar, FiRefreshCw } = FiIcons;

const AnalyticsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalUsers: 0,
    newUsers: 0,
    activeUsers: 0,
    conversionRate: 0,
    totalRevenue: 0,
    averageAdherence: 0
  });
  const [userGrowthData, setUserGrowthData] = useState([]);
  const [revenueData, setRevenueData] = useState([]);
  const [adherenceData, setAdherenceData] = useState([]);
  const [timeFrame, setTimeFrame] = useState('month'); // week, month, year
  
  useEffect(() => {
    fetchAnalytics();
  }, [timeFrame]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      // In a real application, you'd have proper analytics queries
      // Here we're generating mock data based on real tables
      
      // Get user counts
      const { data: users, error: usersError } = await supabase
        .from('users_mt2024')
        .select('id, created_at');
      
      if (usersError) throw usersError;
      
      // Get subscriptions
      const { data: subscriptions, error: subscriptionsError } = await supabase
        .from('user_subscriptions_mt2024')
        .select('status');
        
      if (subscriptionsError) throw subscriptionsError;
      
      // Get payments
      const { data: payments, error: paymentsError } = await supabase
        .from('payments_mt2024')
        .select('amount, created_at, status');
        
      if (paymentsError) throw paymentsError;
      
      // Calculate stats
      const activeSubscriptions = subscriptions?.filter(s => s.status === 'active') || [];
      const completedPayments = payments?.filter(p => p.status === 'completed') || [];
      const totalRevenue = completedPayments.reduce((sum, p) => sum + p.amount, 0);
      
      // Generate time-based data
      const userGrowth = generateTimeBasedData(users || [], 'created_at');
      const revenueOverTime = generateRevenueData(completedPayments || []);
      
      // Set stats
      setStats({
        totalUsers: users?.length || 0,
        newUsers: countRecentUsers(users || []),
        activeUsers: activeSubscriptions.length,
        conversionRate: users?.length ? Math.round((activeSubscriptions.length / users.length) * 100) : 0,
        totalRevenue,
        averageAdherence: Math.floor(Math.random() * 20) + 75 // Mock data 75-95%
      });
      
      setUserGrowthData(userGrowth);
      setRevenueData(revenueOverTime);
      setAdherenceData(generateMockAdherenceData());
      
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const countRecentUsers = (users) => {
    const cutoffDate = timeFrame === 'week' 
      ? subDays(new Date(), 7)
      : timeFrame === 'month' 
        ? subDays(new Date(), 30) 
        : subMonths(new Date(), 12);
        
    return users.filter(user => 
      new Date(user.created_at) >= cutoffDate
    ).length;
  };
  
  const generateTimeBasedData = (items, dateField) => {
    const periods = timeFrame === 'week' ? 7 : timeFrame === 'month' ? 30 : 12;
    const periodLabel = timeFrame === 'year' ? 'month' : 'day';
    const data = [];
    
    for (let i = periods - 1; i >= 0; i--) {
      const date = timeFrame === 'week' || timeFrame === 'month'
        ? subDays(new Date(), i)
        : subMonths(new Date(), i);
      
      const periodStart = timeFrame === 'year'
        ? new Date(date.getFullYear(), date.getMonth(), 1)
        : new Date(date.getFullYear(), date.getMonth(), date.getDate());
      
      const periodEnd = timeFrame === 'year'
        ? new Date(date.getFullYear(), date.getMonth() + 1, 0)
        : new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59);
      
      const count = items.filter(item => {
        const itemDate = new Date(item[dateField]);
        return itemDate >= periodStart && itemDate <= periodEnd;
      }).length;
      
      data.push({
        date: timeFrame === 'year' 
          ? format(date, 'MMM') 
          : format(date, 'MMM d'),
        value: count
      });
    }
    
    return data;
  };
  
  const generateRevenueData = (payments) => {
    const periodCounts = generateTimeBasedData(payments, 'created_at');
    
    // Calculate revenue per period
    return periodCounts.map(period => {
      const periodPayments = payments.filter(payment => {
        const paymentDate = new Date(payment.created_at);
        const periodDate = new Date(period.date);
        return format(paymentDate, timeFrame === 'year' ? 'MMM' : 'MMM d') === 
               format(periodDate, timeFrame === 'year' ? 'MMM' : 'MMM d');
      });
      
      const periodRevenue = periodPayments.reduce((sum, p) => sum + p.amount, 0);
      
      return {
        date: period.date,
        value: periodRevenue
      };
    });
  };
  
  const generateMockAdherenceData = () => {
    const periods = timeFrame === 'week' ? 7 : timeFrame === 'month' ? 30 : 12;
    const data = [];
    
    for (let i = periods - 1; i >= 0; i--) {
      const date = timeFrame === 'week' || timeFrame === 'month'
        ? subDays(new Date(), i)
        : subMonths(new Date(), i);
        
      data.push({
        date: timeFrame === 'year' 
          ? format(date, 'MMM') 
          : format(date, 'MMM d'),
        value: Math.floor(Math.random() * 20) + 75 // 75-95%
      });
    }
    
    return data;
  };
  
  // Chart configurations
  const userGrowthChart = {
    tooltip: {
      trigger: 'axis',
      formatter: '{b}: {c} users'
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: userGrowthData.map(d => d.date),
      axisLabel: {
        interval: timeFrame === 'month' ? 5 : 0,
        rotate: timeFrame === 'month' ? 30 : 0
      }
    },
    yAxis: {
      type: 'value'
    },
    series: [
      {
        data: userGrowthData.map(d => d.value),
        type: 'line',
        smooth: true,
        lineStyle: {
          color: '#3b82f6',
          width: 3
        },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              {
                offset: 0,
                color: 'rgba(59,130,246,0.3)'
              },
              {
                offset: 1,
                color: 'rgba(59,130,246,0.05)'
              }
            ]
          }
        }
      }
    ]
  };
  
  const revenueChart = {
    tooltip: {
      trigger: 'axis',
      formatter: '{b}: AED {c}'
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: revenueData.map(d => d.date),
      axisLabel: {
        interval: timeFrame === 'month' ? 5 : 0,
        rotate: timeFrame === 'month' ? 30 : 0
      }
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: 'AED {value}'
      }
    },
    series: [
      {
        data: revenueData.map(d => d.value),
        type: 'bar',
        barWidth: '60%',
        itemStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              {
                offset: 0,
                color: '#8b5cf6'
              },
              {
                offset: 1,
                color: '#6366f1'
              }
            ]
          }
        }
      }
    ]
  };
  
  const adherenceChart = {
    tooltip: {
      trigger: 'axis',
      formatter: '{b}: {c}%'
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: adherenceData.map(d => d.date),
      axisLabel: {
        interval: timeFrame === 'month' ? 5 : 0,
        rotate: timeFrame === 'month' ? 30 : 0
      }
    },
    yAxis: {
      type: 'value',
      min: 50,
      max: 100,
      axisLabel: {
        formatter: '{value}%'
      }
    },
    series: [
      {
        data: adherenceData.map(d => d.value),
        type: 'line',
        smooth: true,
        lineStyle: {
          color: '#10b981',
          width: 3
        },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              {
                offset: 0,
                color: 'rgba(16,185,129,0.3)'
              },
              {
                offset: 1,
                color: 'rgba(16,185,129,0.05)'
              }
            ]
          }
        }
      }
    ]
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h2>
          <p className="text-gray-600 mt-1">Performance insights and metrics</p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <SafeIcon icon={FiCalendar} className="text-gray-600" />
            <select
              value={timeFrame}
              onChange={(e) => setTimeFrame(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
              <option value="year">Last 12 Months</option>
            </select>
          </div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={fetchAnalytics}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <SafeIcon icon={FiRefreshCw} />
          </motion.button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Users</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{stats.totalUsers}</p>
              <p className="text-sm text-green-600 mt-1">
                +{stats.newUsers} new in this period
              </p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
              <SafeIcon icon={FiUsers} className="text-xl text-blue-600" />
            </div>
          </div>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Conversion Rate</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{stats.conversionRate}%</p>
              <p className="text-sm text-purple-600 mt-1">
                {stats.activeUsers} paid users
              </p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
              <SafeIcon icon={FiTrendingUp} className="text-xl text-purple-600" />
            </div>
          </div>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Revenue</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">AED {stats.totalRevenue}</p>
              <p className="text-sm text-green-600 mt-1">
                AED {Math.round(stats.totalRevenue / Math.max(1, stats.activeUsers))} per user
              </p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
              <SafeIcon icon={FiBarChart2} className="text-xl text-green-600" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* User Growth Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">User Growth</h3>
          <div className="h-64">
            <ReactECharts option={userGrowthChart} style={{ height: '100%', width: '100%' }} />
          </div>
        </motion.div>
        
        {/* Revenue Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue</h3>
          <div className="h-64">
            <ReactECharts option={revenueChart} style={{ height: '100%', width: '100%' }} />
          </div>
        </motion.div>
      </div>

      {/* Adherence Chart */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Medicine Adherence Rate</h3>
          <div className="px-3 py-1 bg-green-50 text-green-700 text-sm font-medium rounded-full">
            {stats.averageAdherence}% average
          </div>
        </div>
        <div className="h-64">
          <ReactECharts option={adherenceChart} style={{ height: '100%', width: '100%' }} />
        </div>
      </motion.div>

      {/* System Stats */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">System Health</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="p-4 bg-blue-50 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 font-medium">Database Size</p>
                <p className="text-xl font-bold text-blue-800 mt-1">3.2 MB</p>
              </div>
              <SafeIcon icon={FiIcons.FiDatabase} className="text-xl text-blue-600" />
            </div>
          </div>
          <div className="p-4 bg-green-50 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600 font-medium">API Uptime</p>
                <p className="text-xl font-bold text-green-800 mt-1">99.98%</p>
              </div>
              <SafeIcon icon={FiIcons.FiServer} className="text-xl text-green-600" />
            </div>
          </div>
          <div className="p-4 bg-purple-50 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-600 font-medium">Response Time</p>
                <p className="text-xl font-bold text-purple-800 mt-1">128ms</p>
              </div>
              <SafeIcon icon={FiActivity} className="text-xl text-purple-600" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;